import React, { useEffect, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Col,
  Divider,
  Input,
  Modal,
  Popover,
  Row,
  Select,
  Space,
  Table,
  Tag,
  message,
} from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import accountApi from "../../accounts/accounts.service";
import { PermissionDTO } from "../../auth/context/useAuthContext";
import {
  PREFIX_ADMIN_PERMISSIONS,
  withoutRenderPermission,
} from "../../../utils/PermissionList";
import { PermissionItem } from "./PermissionItem";
import { matchesPermissionSearch } from "./permissionPresentation";

interface AdminAccount {
  id: string;
  name: string;
  email?: string;
  band: { name: string };
  permission: PermissionDTO[];
}

type ApplyStatus = "idle" | "applying" | "success" | "fail";

interface BatchRow {
  identifier: string;
  candidates: AdminAccount[];
  selectedId?: string;
  checked: PermissionDTO[];
  applyStatus: ApplyStatus;
  applyError?: string;
}

export interface BatchPermissionModalProps {
  isVisible: boolean;
  onClose: () => void;
  setForceReload: React.DispatchWithoutAction;
}

interface PermissionChecklistProps {
  permissions: PermissionDTO[];
  checked: PermissionDTO[];
  // 계정의 현재 권한. 주어지면 추가/제거 예정 항목을 색으로 표시한다
  baseline?: PermissionDTO[];
  onChange: (next: PermissionDTO[]) => void;
}

const chipStyles = {
  // 유지: 체크됨 + 현재도 보유
  kept: { background: `#e6f7ff`, border: `1px solid #91d5ff` },
  // 추가 예정: 체크됨 + 현재 미보유
  added: { background: `#f6ffed`, border: `1px solid #b7eb8f` },
  // 제거 예정: 체크 해제 + 현재 보유
  removed: { background: `#fff1f0`, border: `1px solid #ffa39e` },
  // 무관: 체크 해제 + 현재 미보유
  none: { background: `transparent`, border: `1px solid transparent` },
};

const PermissionChecklist = ({
  permissions,
  checked,
  baseline,
  onChange,
}: PermissionChecklistProps) => {
  const [filter, setFilter] = useState("");

  const checkAll =
    permissions.length > 0 && checked.length === permissions.length;
  const indeterminate =
    checked.length > 0 && checked.length < permissions.length;

  const baselineNames = baseline
    ? new Set(baseline.map((item) => item.name))
    : undefined;
  const checkedNames = new Set(checked.map((item) => item.name));

  const togglePermission = (item: PermissionDTO) => {
    onChange(
      checkedNames.has(item.name)
        ? checked.filter((c) => c.name !== item.name)
        : [...checked, item]
    );
  };

  const chipStyleOf = (item: PermissionDTO): React.CSSProperties => {
    const isChecked = checkedNames.has(item.name);
    if (!baselineNames) {
      return isChecked ? chipStyles.kept : chipStyles.none;
    }
    const owned = baselineNames.has(item.name);
    if (isChecked && owned) return chipStyles.kept;
    if (isChecked && !owned) return chipStyles.added;
    if (!isChecked && owned) return chipStyles.removed;
    return chipStyles.none;
  };

  const visibleItems = permissions.filter((item) =>
    matchesPermissionSearch(item, filter)
  );

  return (
    <Row gutter={[8, 8]}>
      <Col span={24}>
        <Space wrap>
          <Checkbox
            indeterminate={indeterminate}
            checked={checkAll}
            onChange={(e: CheckboxChangeEvent) =>
              onChange(e.target.checked ? permissions : [])
            }
          >
            전체선택
          </Checkbox>
          {PREFIX_ADMIN_PERMISSIONS.map((preset: any, i: number) => (
            <Button
              size={"small"}
              key={i}
              onClick={() =>
                onChange(
                  preset.value.filter((item: PermissionDTO) =>
                    permissions.some((p) => p.name === item.name)
                  )
                )
              }
            >
              {preset.name}
            </Button>
          ))}
          <Input
            size="small"
            allowClear
            style={{ width: 200 }}
            placeholder="권한 이름·설명 검색"
            aria-label="권한 이름·설명 검색"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span style={{ color: "#595959" }}>
            {checked.length}/{permissions.length}개 선택
          </span>
          {baselineNames && (
            <Space size={4}>
              <Tag color="green" style={{ margin: 0 }}>추가 예정</Tag>
              <Tag color="red" style={{ margin: 0 }}>제거 예정</Tag>
            </Space>
          )}
        </Space>
      </Col>
      <Col span={24}>
        <Divider style={{ margin: `4px 0` }} />
      </Col>
      <Col span={24}>
        <div style={{ maxHeight: "min(60vh, 560px)", overflowY: `auto` }}>
          <Row gutter={[12, 12]}>
            {visibleItems.length === 0 && (
              <Col span={24}>
                <span style={{ color: `#999` }}>검색 결과가 없습니다.</span>
              </Col>
            )}
            {visibleItems.map((item) => (
              <Col xs={24} md={12} xl={8} key={item.name}>
                <div
                  style={{
                    padding: `10px 12px`,
                    borderRadius: 6,
                    ...chipStyleOf(item),
                  }}
                >
                  <PermissionItem
                    permission={item}
                    checked={checkedNames.has(item.name)}
                    onChange={() => togglePermission(item)}
                    changeStatus={
                      baselineNames
                        ? checkedNames.has(item.name) && !baselineNames.has(item.name)
                          ? "added"
                          : !checkedNames.has(item.name) && baselineNames.has(item.name)
                          ? "removed"
                          : undefined
                        : undefined
                    }
                  />
                </div>
              </Col>
            ))}
          </Row>
        </div>
      </Col>
    </Row>
  );
};

export const BatchPermissionModal = ({
  isVisible,
  onClose,
  setForceReload,
}: BatchPermissionModalProps) => {
  const [inputText, setInputText] = useState("");
  const [rows, setRows] = useState<BatchRow[]>([]);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupProgress, setLookupProgress] = useState("");
  const [applyAllLoading, setApplyAllLoading] = useState(false);
  const [appliedAny, setAppliedAny] = useState(false);

  const [permissions, setPermissions] = useState<PermissionDTO[]>([]);

  // 일괄 적용 전용 체크 상태 (개별 행 체크 상태와 별개)
  const [bulkChecked, setBulkChecked] = useState<PermissionDTO[]>([]);

  useEffect(() => {
    (async () => {
      const result = await accountApi.getPermissions("Admin");
      setPermissions(
        result.filter(
          (item: PermissionDTO) => !withoutRenderPermission.includes(item.name)
        )
      );
    })();
  }, []);

  // 그리드에 렌더링되는 권한만 편집 대상이다. 그 외(숨김 권한, 타 타입 등
  // getPermissions 응답에 없는 권한)는 편집 불가이므로 적용 시 무조건 보존한다.
  const renderableNames = new Set(permissions.map((item) => item.name));

  const toVisiblePermissions = (account: AdminAccount) =>
    account.permission.filter((item) => renderableNames.has(item.name));

  const toPreservedPermissions = (account: AdminAccount) =>
    account.permission.filter((item) => !renderableNames.has(item.name));

  const patchRow = (identifier: string, patch: Partial<BatchRow>) => {
    setRows((prev) =>
      prev.map((row) =>
        row.identifier === identifier ? { ...row, ...patch } : row
      )
    );
  };

  const getSelectedAccount = (row: BatchRow) =>
    row.candidates.find((candidate) => candidate.id === row.selectedId);

  const handleLookup = async () => {
    const identifiers = Array.from(
      new Set(
        inputText
          .split(/[\n,;\t ]+/)
          .map((value) => value.trim().toLowerCase())
          .filter(Boolean)
      )
    );
    if (identifiers.length === 0) {
      message.warning("이메일 리스트를 입력해주세요.");
      return;
    }
    if (permissions.length === 0) {
      message.warning("권한 목록을 아직 불러오지 못했습니다. 잠시 후 다시 시도해주세요.");
      return;
    }

    setLookupLoading(true);
    const nextRows: BatchRow[] = [];
    for (let i = 0; i < identifiers.length; i++) {
      setLookupProgress(`${i + 1}/${identifiers.length}`);
      const identifier = identifiers[i];
      try {
        const resp = await accountApi.getAdmins({
          page: 0,
          size: 50,
          search: identifier,
        });
        const items: AdminAccount[] = resp?.items ?? [];
        const exactMatches = items.filter(
          (item) => item.email?.toLowerCase() === identifier
        );
        const candidates = exactMatches.length > 0 ? exactMatches : items;
        const selected =
          candidates.length === 1 ? candidates[0] : undefined;
        nextRows.push({
          identifier,
          candidates,
          selectedId: selected?.id,
          checked: selected ? toVisiblePermissions(selected) : [],
          applyStatus: "idle",
        });
      } catch (e) {
        console.error(e);
        nextRows.push({
          identifier,
          candidates: [],
          checked: [],
          applyStatus: "idle",
          applyError: "조회 실패",
        });
      }
    }
    setRows(nextRows);
    setLookupLoading(false);
    setLookupProgress("");
  };

  const buildPermissionNames = (
    account: AdminAccount,
    checked: PermissionDTO[]
  ) => {
    const checkedNames = checked
      .map((item) => item.name)
      .filter((name) => renderableNames.has(name));
    return [
      ...checkedNames,
      ...toPreservedPermissions(account).map((item) => item.name),
    ];
  };

  const applyRow = async (
    row: BatchRow,
    checked: PermissionDTO[]
  ): Promise<boolean> => {
    const account = getSelectedAccount(row);
    if (!account) return false;
    patchRow(row.identifier, { applyStatus: "applying", applyError: undefined });
    try {
      const result = await accountApi.updateAdminPermissions(account.id, {
        permissions: buildPermissionNames(account, checked),
      });
      if (result.status !== 200) throw result;
      const appliedPermission = [
        ...checked.filter((item) => renderableNames.has(item.name)),
        ...toPreservedPermissions(account),
      ];
      patchRow(row.identifier, {
        applyStatus: "success",
        checked,
        candidates: row.candidates.map((candidate) =>
          candidate.id === account.id
            ? { ...candidate, permission: appliedPermission }
            : candidate
        ),
      });
      setAppliedAny(true);
      return true;
    } catch (e: any) {
      console.error(e);
      patchRow(row.identifier, {
        applyStatus: "fail",
        applyError:
          e?.response?.data?.message ?? "알 수 없는 에러가 발생했습니다.",
      });
      return false;
    }
  };

  const mergeByName = (base: PermissionDTO[], add: PermissionDTO[]) => {
    const baseNames = new Set(base.map((item) => item.name));
    return [...base, ...add.filter((item) => !baseNames.has(item.name))];
  };

  const handleApplyAll = (mode: "replace" | "merge") => {
    const targetRows = rows.filter((row) => row.selectedId);
    if (targetRows.length === 0) {
      message.warning("적용할 계정이 없습니다.");
      return;
    }
    const isEmptyReplace = mode === "replace" && bulkChecked.length === 0;
    Modal.confirm({
      title: isEmptyReplace
        ? `${targetRows.length}개 계정의 권한을 모두 제거할까요?`
        : mode === "replace"
        ? `${targetRows.length}개 계정의 권한을 전체 대체할까요?`
        : `${targetRows.length}개 계정에 선택한 권한 ${bulkChecked.length}개를 켤까요?`,
      content: isEmptyReplace
        ? "체크된 권한이 0개입니다. 진행하면 매칭된 모든 계정의 권한이 전부 제거됩니다."
        : mode === "replace"
        ? "모든 계정의 권한이 체크된 권한과 동일하게 대체됩니다. 계정별로 있던 다른 권한은 제거됩니다."
        : "체크된 권한만 각 계정에 추가(ON)됩니다. 계정이 기존에 갖고 있던 다른 권한은 그대로 유지됩니다.",
      okText: mode === "replace" ? "전체 대체" : "선택 권한만 ON",
      okButtonProps: mode === "replace" ? { danger: true } : undefined,
      cancelText: "취소",
      onOk: async () => {
        setApplyAllLoading(true);
        let successCount = 0;
        for (const row of targetRows) {
          const account = getSelectedAccount(row);
          if (!account) continue;
          const nextChecked =
            mode === "replace"
              ? bulkChecked
              : mergeByName(toVisiblePermissions(account), bulkChecked);
          const ok = await applyRow(row, nextChecked);
          if (ok) successCount += 1;
        }
        setApplyAllLoading(false);
        if (successCount === targetRows.length) {
          message.success(`권한 일괄 적용 완료 (${successCount}건).`);
        } else {
          message.error(
            `성공 ${successCount}건 / 실패 ${
              targetRows.length - successCount
            }건. 실패 행을 확인해주세요.`
          );
        }
      },
    });
  };

  const handleClose = () => {
    if (appliedAny) setForceReload();
    onClose();
  };

  const renderPermissionCount = (account: AdminAccount) => {
    const visiblePermissions = toVisiblePermissions(account);
    return (
      <Popover
        overlayStyle={{ maxWidth: 480 }}
        content={
          visiblePermissions.length > 0 ? (
            <Space wrap size={[4, 4]}>
              {visiblePermissions.map((item) => (
                <Tag key={item.name}>{item.displayName}</Tag>
              ))}
            </Space>
          ) : (
            "권한 없음"
          )
        }
      >
        <Button size="small">{visiblePermissions.length}개</Button>
      </Popover>
    );
  };

  const renderDiff = (row: BatchRow, account: AdminAccount) => {
    const checkedNames = new Set(row.checked.map((item) => item.name));
    const currentNames = new Set(account.permission.map((item) => item.name));
    const added = row.checked.filter((item) => !currentNames.has(item.name));
    const removed = toVisiblePermissions(account).filter(
      (item) => !checkedNames.has(item.name)
    );
    if (added.length === 0 && removed.length === 0) {
      return <Tag>변경 없음</Tag>;
    }
    return (
      <Popover
        overlayStyle={{ maxWidth: 480 }}
        content={
          <Space direction="vertical">
            {added.length > 0 && (
              <Space wrap size={[4, 4]}>
                {added.map((item) => (
                  <Tag color="green" key={item.name}>
                    + {item.displayName}
                  </Tag>
                ))}
              </Space>
            )}
            {removed.length > 0 && (
              <Space wrap size={[4, 4]}>
                {removed.map((item) => (
                  <Tag color="red" key={item.name}>
                    - {item.displayName}
                  </Tag>
                ))}
              </Space>
            )}
          </Space>
        }
      >
        <Button size="small">
          <span style={{ color: "#389e0d" }}>+{added.length}</span>
          {" / "}
          <span style={{ color: "#cf1322" }}>-{removed.length}</span>
        </Button>
      </Popover>
    );
  };

  const sectionBoxStyle: React.CSSProperties = {
    border: `1px solid #d9d9d9`,
    borderRadius: 8,
    padding: 16,
    background: `#fff`,
  };

  const expandedRowRender = (row: BatchRow) => {
    const account = getSelectedAccount(row);
    if (!account) return null;

    const baseline = toVisiblePermissions(account);
    const baselineNames = new Set(baseline.map((item) => item.name));
    const checkedNames = new Set(row.checked.map((item) => item.name));
    const addedCount = row.checked.filter(
      (item) => !baselineNames.has(item.name)
    ).length;
    const removedCount = baseline.filter(
      (item) => !checkedNames.has(item.name)
    ).length;
    const hasChanges = addedCount > 0 || removedCount > 0;

    return (
      <div style={{ ...sectionBoxStyle, margin: `8px 0` }}>
        <Row gutter={[8, 16]}>
          <Col span={24}>
            <Space wrap>
              <strong>
                {account.name} ({account.email ?? "이메일 없음"})
              </strong>
              {hasChanges ? (
                <Space size={4}>
                  <Tag color="green" style={{ margin: 0 }}>
                    +{addedCount}
                  </Tag>
                  <Tag color="red" style={{ margin: 0 }}>
                    -{removedCount}
                  </Tag>
                </Space>
              ) : (
                <Tag style={{ margin: 0 }}>변경 없음</Tag>
              )}
              <Button
                size="small"
                disabled={!hasChanges}
                onClick={() =>
                  patchRow(row.identifier, {
                    checked: baseline,
                    applyStatus: "idle",
                    applyError: undefined,
                  })
                }
              >
                현재 권한으로 되돌리기
              </Button>
              <Button
                size="small"
                type="primary"
                loading={row.applyStatus === "applying"}
                disabled={applyAllLoading || !hasChanges}
                onClick={() => applyRow(row, row.checked)}
              >
                이 계정에 적용
              </Button>
            </Space>
          </Col>
          <Col span={24}>
            <Divider style={{ margin: 0 }} />
          </Col>
          <Col span={24}>
            <PermissionChecklist
              permissions={permissions}
              checked={row.checked}
              baseline={baseline}
              onChange={(next) =>
                patchRow(row.identifier, {
                  checked: next,
                  applyStatus: "idle",
                  applyError: undefined,
                })
              }
            />
          </Col>
        </Row>
      </div>
    );
  };

  const columns = [
    {
      title: "입력값",
      dataIndex: "identifier",
      width: 220,
    },
    {
      title: "계정",
      width: 260,
      render: (_: unknown, row: BatchRow) => {
        if (row.candidates.length === 0) {
          return <Tag color="red">{row.applyError ?? "계정 없음"}</Tag>;
        }
        if (row.candidates.length === 1) {
          const account = row.candidates[0];
          return (
            <span>
              {account.name} ({account.band.name})
            </span>
          );
        }
        return (
          <Select
            style={{ width: "100%" }}
            placeholder={`${row.candidates.length}개 계정 매칭 — 선택`}
            value={row.selectedId}
            onChange={(value) => {
              const account = row.candidates.find(
                (candidate) => candidate.id === value
              );
              patchRow(row.identifier, {
                selectedId: value,
                checked: account ? toVisiblePermissions(account) : [],
                applyStatus: "idle",
                applyError: undefined,
              });
            }}
            options={row.candidates.map((candidate) => ({
              value: candidate.id,
              label: `${candidate.name} (${
                candidate.email ?? "이메일 없음"
              } / ${candidate.band.name})`,
            }))}
          />
        );
      },
    },
    {
      title: "현재 권한",
      width: 100,
      align: "center" as const,
      render: (_: unknown, row: BatchRow) => {
        const account = getSelectedAccount(row);
        return account ? renderPermissionCount(account) : "-";
      },
    },
    {
      title: "변경 예정",
      width: 120,
      align: "center" as const,
      render: (_: unknown, row: BatchRow) => {
        const account = getSelectedAccount(row);
        return account ? renderDiff(row, account) : "-";
      },
    },
    {
      title: "상태",
      width: 140,
      align: "center" as const,
      render: (_: unknown, row: BatchRow) => {
        if (row.applyStatus === "success")
          return <Tag color="green">적용 완료</Tag>;
        if (row.applyStatus === "applying")
          return <Tag color="blue">적용 중</Tag>;
        if (row.applyStatus === "fail")
          return <Tag color="red">{row.applyError ?? "실패"}</Tag>;
        return <Tag>대기</Tag>;
      },
    },
  ];

  const matchedCount = rows.filter((row) => row.selectedId).length;

  return (
    <Modal
      title={<p style={{ margin: 0 }}>권한 일괄 수정 (이메일 리스트)</p>}
      onCancel={handleClose}
      width={"98%"}
      visible={isVisible}
      footer={<Button onClick={handleClose}>닫기</Button>}
    >
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Input.TextArea
            rows={4}
            placeholder={
              "이메일 리스트를 붙여넣으세요. (줄바꿈/쉼표/공백 구분)\n예)\njiwoong@theswing.co.kr\nsomeone@theswing.co.kr"
            }
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
          />
        </Col>
        <Col span={24}>
          <Button type="primary" loading={lookupLoading} onClick={handleLookup}>
            {lookupLoading ? `계정 조회 중 ${lookupProgress}` : "계정 조회"}
          </Button>
        </Col>
        {rows.length > 0 && (
          <>
            <Col span={24}>
              <Alert
                type="info"
                showIcon
                message="행을 펼치면 해당 계정의 현재 권한이 체크된 상태로 표시됩니다. 계정별로 편집 후 '이 계정에 적용'을 누르세요."
              />
            </Col>
            <Col span={24}>
              <Table
                size="small"
                rowKey={(row) => row.identifier}
                columns={columns}
                dataSource={rows}
                pagination={false}
                expandable={{
                  expandedRowRender,
                  rowExpandable: (row) => !!getSelectedAccount(row),
                }}
                scroll={{ x: "max-content" }}
              />
            </Col>
            <Col span={24}>
              <div style={sectionBoxStyle}>
                <Row gutter={[8, 16]}>
                  <Col span={24}>
                    <Space align="center" wrap>
                      <strong>일괄 적용 권한 설정</strong>
                      <Button
                        type="primary"
                        loading={applyAllLoading}
                        disabled={matchedCount === 0 || bulkChecked.length === 0}
                        onClick={() => handleApplyAll("merge")}
                      >
                        선택 권한만 {matchedCount}개 계정에 ON (기존 유지)
                      </Button>
                      <Button
                        type="primary"
                        danger
                        loading={applyAllLoading}
                        disabled={matchedCount === 0}
                        onClick={() => handleApplyAll("replace")}
                      >
                        {matchedCount}개 계정 권한 전체 대체
                      </Button>
                    </Space>
                  </Col>
                  <Col span={24}>
                    <Alert
                      type="warning"
                      showIcon
                      message="'선택 권한만 ON'은 체크한 권한을 각 계정에 추가만 하고 기존 권한은 유지합니다. '전체 대체'는 모든 계정의 권한을 체크 상태와 동일하게 덮어써 기존 권한이 제거될 수 있습니다."
                    />
                  </Col>
                  <Col span={24}>
                    <Divider style={{ margin: 0 }} />
                  </Col>
                  <Col span={24}>
                    <PermissionChecklist
                      permissions={permissions}
                      checked={bulkChecked}
                      onChange={setBulkChecked}
                    />
                  </Col>
                </Row>
              </div>
            </Col>
          </>
        )}
      </Row>
    </Modal>
  );
};
